import {MouseEventHandler, ReactElement, ReactNode} from 'react';
import {createRoot, Root} from 'react-dom/client';
import {DeepPartial} from 'redux';
import {t} from '../common/getTranslations';
import {ButtonV2Variant} from '../design-system/atoms/ButtonV2/ButtonV2.enum';
import {Dialog} from '../design-system/organisms/Dialog';
import {EventLog} from '../graphql/generated/types';
import {aggregateAndSendLog} from '../logging/aggregateAndSendLog';
import {putTiaraObjectProps} from '../logging/putTiaraObjectProps';
import {TrackEventObject} from '../logging/TrackEventObject.interface';
import {SIMPLE_DIALOG_WRAPPER_ID} from './constants';
import {createWrapperDiv} from './createWrapperDiv';

let rootContainer: Root | null;

export function showStandaloneDialog({
  dialogComponent,
}: {
  dialogComponent: ReactElement;
}) {
  removeSimpleDialog();
  const container = createWrapperDiv({
    id: SIMPLE_DIALOG_WRAPPER_ID,
  });
  rootContainer = createRoot(container);
  rootContainer.render(dialogComponent);
}

/**
 * 간단하게 미리 정의된 Dialog 를 띄우는 함수입니다
 */
export function showSimpleDialog({
  title,
  description,
  isMobile,
  okButton,
  cancelButton,
  shouldCloseOnDimmedClick = true,
  onClose,
  eventLog,
  onInView,
  dialogTitleClassName,
}: {
  title: string | ReactNode;
  description?: string | ReactNode;
  isMobile: boolean;
  /**
   * 다이얼로그 텍스트 버튼입니다. '확인', '새로고침' 등 버튼을 표현할 때 쓰입니다.
   */
  okButton?: {
    text: string;
    type: 'label' | 'button';
    onClick?: MouseEventHandler;
    closeAfterClick?: boolean;
  };
  cancelButton?: {
    text?: string;
    type: 'label' | 'button';
    onClick?: MouseEventHandler;
  };
  shouldCloseOnDimmedClick?: boolean;
  onClose?: () => void;
  eventLog?: DeepPartial<TrackEventObject | EventLog>;
  onInView?: () => void;
  dialogTitleClassName?: string;
}) {
  const dialogComponent = (
    <Dialog
      isOpen={true}
      onClose={() => {
        onClose?.();
        removeSimpleDialog();
      }}
      isMobile={isMobile}
      {...putTiaraObjectProps(eventLog)}
      isUsingScrollBlock={false} // KW-8238 react-virtualized 와 모달 body fixed 변경 시 리스트가 안보이는 현상이 있어 뒷면 스크롤 방지 기능 제거
      shouldCloseOnDimmedClick={shouldCloseOnDimmedClick}
      onInView={
        onInView ??
        (eventLog?.viewimp_contents
          ? target => {
              aggregateAndSendLog({
                targetElem: target as HTMLElement,
                actionType: 'ViewImp',
                actionName: '팝업_노출',
                customProps: {
                  user_action: 'imp',
                },
              });
            }
          : undefined)
      }
    >
      <Dialog.Dimmed />
      <Dialog.Title className={dialogTitleClassName}>{title}</Dialog.Title>
      {description && (
        <Dialog.List>
          <Dialog.Label>{description}</Dialog.Label>
        </Dialog.List>
      )}
      <Dialog.BottomCombination>
        {cancelButton && (
          <Dialog.Button
            onClick={e => {
              cancelButton.onClick?.(e);
              removeSimpleDialog();
            }}
            variant={ButtonV2Variant.Secondary}
          >
            {cancelButton.text || t('common_cancel_button')}
          </Dialog.Button>
        )}
        {okButton && (
          <Dialog.Button
            onClick={
              okButton.closeAfterClick
                ? e => {
                    okButton?.onClick?.(e);
                    removeSimpleDialog();
                  }
                : okButton?.onClick
            }
            variant={
              cancelButton ? ButtonV2Variant.Primary : ButtonV2Variant.Secondary
            }
          >
            {okButton.text}
          </Dialog.Button>
        )}
      </Dialog.BottomCombination>
    </Dialog>
  );
  showStandaloneDialog({dialogComponent});
}

export function removeSimpleDialog() {
  const container = document.getElementById(SIMPLE_DIALOG_WRAPPER_ID);
  if (container) {
    if (rootContainer) {
      rootContainer.unmount();
      rootContainer = null;
    }
    container.parentNode?.removeChild(container);
  }
}
